import { navigationEpoch } from '../../navigation/loadEpoch';
import { getPersistenceNamespace } from '../helpers/persistenceHelpers';
import type { StateCreator } from 'zustand';
import type {
  DataDomain,
  Perspective,
  SemanticModel,
  TreeNode,
  QueryFilter,
  QueryResult,
  ErrorDetails,
  MetricInstance,
  MetricConfig,
  FilterGroup,
  FilterCondition,
  CalculatedAttribute,
  OrderBySpec,
  OrderByDirection,
  PinnedResult,
  QueryTemplate,
  DateRangePreset,
  WorkspaceAgentPatch,
} from '../../types';
import type { VoyagerState } from '../useVoyagerStore';
import {
  getDomains,
  getPerspectives,
  getAllPerspectives,
  getModel,
  generateQuery,
  executeQuery,
} from '../../apis';
import { buildTreeFromModel, generateId, resolvePresetDates, getMetricPrimarySortColumn, getDimensionOutputAlias } from '../../utils';
import { saveUserSettings } from '../helpers/persistenceHelpers';
import {
  createEmptyFilterGroup,
  createFilterCondition,
  createDefaultFilterConditions,
  addToGroup,
  removeFromGroup,
  updateInGroup,
  stripDefaultFilters,
  hasConditions,
  serializeFilterGroup,
  filtersToGroup,
} from '../helpers/filterHelpers';

export interface WorkspaceSlice {
  // Domain & Perspective
  domains: DataDomain[];
  perspectives: Perspective[];
  selectedDomain: string | null;
  selectedPerspective: string | null;

  // Semantic Model
  semanticModel: SemanticModel | null;
  treeNodes: TreeNode[];

  // Selections
  selectedMetrics: MetricInstance[];
  selectedDimensions: string[];
  metricConfigs: Record<string, MetricConfig>;
  calculatedAttributes: CalculatedAttribute[];
  filters: QueryFilter[];
  filterGroup: FilterGroup;
  defaultFilterIds: Set<string>;

  // Sort / ORDER BY — user-dictated, never auto-generated
  orderBy: OrderBySpec[];

  // Query
  querySQL: string;
  queryResults: QueryResult | null;

  // UI State
  isLoading: boolean;
  /** Where the current Run is — the results tracker's source, apart from other loading (#565). */
  queryPhase: QueryPhase;
  error: string | null;
  errorDetails: ErrorDetails | null;
  expandedNodes: Set<string>;
  focusedNodeId: string | null;

  // Pinned Results
  pinnedResults: PinnedResult[];
  activeResultTab: string | null;

  // Panel visibility
  isModelBrowserOpen: boolean;

  // Active date field
  activeDateField: string | null;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  dateRangePreset: DateRangePreset;

  // Actions
  fetchDomains: (signal?: AbortSignal) => Promise<void>;
  selectDomain: (domainId: string, signal?: AbortSignal) => Promise<void>;
  selectPerspective: (perspectiveId: string, signal?: AbortSignal) => Promise<void>;
  loadModel: (perspectiveId: string, signal?: AbortSignal) => Promise<void>;
  toggleMetric: (metricName: string) => void;
  removeMetricInstance: (id: string) => void;
  toggleDimension: (dimensionName: string) => void;
  setMetricAlias: (instanceId: string, alias: string) => void;
  setMetricAggregationOverride: (instanceId: string, agg: string | undefined) => void;
  setMetricTimePeriod: (instanceId: string, period: string | undefined) => void;
  setMetricDateDimension: (instanceId: string, dateDim: string | undefined) => void;
  addFilter: (filter: QueryFilter) => void;
  removeFilter: (index: number) => void;
  addFilterCondition: (parentGroupId: string, column: string, operator: string, value: string, value2?: string) => void;
  addFilterGroup: (parentGroupId: string) => void;
  removeFilterNode: (nodeId: string) => void;
  updateFilterCondition: (conditionId: string, updates: Partial<FilterCondition>) => void;
  toggleFilterGroupLogic: (groupId: string) => void;
  toggleFilterGroupNegated: (groupId: string) => void;
  setFilterGroup: (group: FilterGroup) => void;
  addCalculatedField: (field: { name: string; formula: string; type?: 'metric' | 'dimension' }) => void;
  updateCalculatedField: (id: string, updates: { name?: string; formula?: string; type?: 'metric' | 'dimension' }) => void;
  removeCalculatedField: (id: string) => void;
  generateQuery: () => Promise<void>;
  executeQuery: () => Promise<void>;
  navigateToNode: (nodeId: string) => void;
  toggleModelBrowser: () => void;
  pinCurrentResults: () => void;
  unpinResult: (id: string) => void;
  renamePin: (id: string, label: string) => void;
  setActiveResultTab: (id: string | null) => void;
  applyTemplate: (template: QueryTemplate) => void;
  clearSelections: () => void;
  applyAgentPatchToWorkspace: (patch: WorkspaceAgentPatch) => Promise<void>;
  setActiveDateField: (field: string | null) => void;
  setDateRange: (start: string | null, end: string | null) => void;
  setDateRangePreset: (preset: DateRangePreset) => void;

  // Sort actions
  setOrderByDirection: (column: string, direction: OrderByDirection | null) => void;
  cycleOrderBy: (column: string) => void;
  moveOrderBy: (column: string, delta: number) => void;
  removeOrderBy: (column: string) => void;
  clearOrderBy: () => void;
}

export type QueryPhase = 'idle' | 'generating' | 'executing';

// Only the latest Run may move the phase, so a superseded or stale run cannot strand it.
let queryRun = 0;

export const createWorkspaceSlice: StateCreator<
  VoyagerState, [], [], WorkspaceSlice
> = (set, get) => ({
  domains: [],
  perspectives: [],
  selectedDomain: null,
  selectedPerspective: null,
  semanticModel: null,
  treeNodes: [],
  selectedMetrics: [],
  selectedDimensions: [],
  metricConfigs: {},
  calculatedAttributes: [],
  filters: [],
  filterGroup: createEmptyFilterGroup(),
  defaultFilterIds: new Set<string>(),
  orderBy: [],
  querySQL: '',
  queryResults: null,
  activeDateField: null,
  dateRangeStart: null,
  dateRangeEnd: null,
  dateRangePreset: 'YESTERDAY' as DateRangePreset,
  isLoading: false,
  queryPhase: 'idle' as QueryPhase,
  error: null,
  errorDetails: null,
  expandedNodes: new Set<string>(),
  focusedNodeId: null,
  pinnedResults: [],
  activeResultTab: null,
  isModelBrowserOpen: true,

  // ----------------------------------------------------------
  // Domain & Perspective actions
  // ----------------------------------------------------------

  fetchDomains: async (signal?: AbortSignal) => {
    const namespace = getPersistenceNamespace();
    const loadEpoch = navigationEpoch();
    set({ isLoading: true, error: null });
    try {
      // Without the domain layer (#464) the picker IS the perspective list.
      const { domains: domainsOn } = await get().ensureFeatures();
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      if (!domainsOn) {
        const perspectives = await getAllPerspectives();
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
        set({ domains: [], perspectives, isLoading: false });
        return;
      }
      const domains = await getDomains();
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({ domains, isLoading: false });
    } catch (err: any) {
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({
        error: err?.message || 'Failed to fetch domains',
        isLoading: false,
      });
    }
  },

  selectDomain: async (domainId: string, signal?: AbortSignal) => {
    const namespace = getPersistenceNamespace();
    const loadEpoch = navigationEpoch();
    const clearWsAgent = (get() as any).clearWorkspaceAgentHistory;
    if (typeof clearWsAgent === 'function') clearWsAgent();
    set({
      selectedDomain: domainId,
      selectedPerspective: null,
      semanticModel: null,
      treeNodes: [],
      selectedMetrics: [],
      selectedDimensions: [],
      metricConfigs: {},
      calculatedAttributes: [],
      filters: [],
      filterGroup: createEmptyFilterGroup(),
      defaultFilterIds: new Set<string>(),
      orderBy: [],
      querySQL: '',
      queryResults: null,
      loadedReportId: null,
      loadedReportName: null,
      isLoading: true,
      error: null,
    });
    try {
      const perspectives = await getPerspectives(domainId);
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({ perspectives, isLoading: false });
    } catch (err: any) {
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({
        error: err?.message || 'Failed to fetch perspectives',
        isLoading: false,
      });
    }
  },

  selectPerspective: async (perspectiveId: string, signal?: AbortSignal) => {
    const clearWsAgent = (get() as any).clearWorkspaceAgentHistory;
    if (typeof clearWsAgent === 'function') clearWsAgent();
    // Reset draft mode when switching perspectives
    const { activeDraftId, userSettings, features, perspectives } = get();
    // Without the domain layer (#464) nobody picks a domain: it is whatever the
    // perspective says, so saved reports and threads keep their field. The
    // perspective is then the unit a switch crosses, so the loaded report's
    // identity goes with it — as it did when crossing a domain.
    const derivedDomain = features.domains
      ? {}
      : { selectedDomain: perspectives.find((p) => p.id === perspectiveId)?.domain ?? null,
          loadedReportId: null, loadedReportName: null };
    if (activeDraftId) {
      const newSettings = { ...userSettings, modelSource: 'production' as const };
      saveUserSettings(newSettings);
      set({
        selectedPerspective: perspectiveId,
        ...derivedDomain,
        userSettings: newSettings,
        activeDraftId: null,
        activeDraftName: null,
        availableDrafts: [],
      });
    } else {
      set({ selectedPerspective: perspectiveId, ...derivedDomain });
    }
    if (signal) await get().loadModel(perspectiveId, signal);
    else await get().loadModel(perspectiveId);
  },

  // ----------------------------------------------------------
  // Model loading
  // ----------------------------------------------------------

  loadModel: async (perspectiveId: string, signal?: AbortSignal) => {
    const namespace = getPersistenceNamespace();
    const loadEpoch = navigationEpoch();
    set({
      isLoading: true,
      error: null,
      selectedMetrics: [],
      selectedDimensions: [],
      metricConfigs: {},
      calculatedAttributes: [],
      filters: [],
      filterGroup: createEmptyFilterGroup(),
      defaultFilterIds: new Set<string>(),
      orderBy: [],
      querySQL: '',
      queryResults: null,
    });
    try {
      const { userSettings, activeDraftId } = get();
      const isDraftMode = userSettings.modelSource === 'draft' && activeDraftId;
      const model = isDraftMode
        ? await getModel(perspectiveId, { source: 'draft', draftId: activeDraftId })
        : await getModel(perspectiveId);
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      const treeNodes = buildTreeFromModel(model);

      // Only expand root node; children stay collapsed
      const expanded = new Set<string>();
      treeNodes.forEach((root) => {
        expanded.add(root.id);
      });

      // Inject default filters as locked conditions
      let filterGroup = createEmptyFilterGroup();
      let defaultFilterIds = new Set<string>();
      if (model.default_filters && model.default_filters.length > 0) {
        const { conditions, ids } = createDefaultFilterConditions(model.default_filters);
        filterGroup = { ...filterGroup, children: [...conditions] };
        defaultFilterIds = ids;
      }

      const defaultPreset: DateRangePreset = 'YESTERDAY';
      const presetDates = model.date_field ? resolvePresetDates(defaultPreset) : null;

      set({
        semanticModel: model,
        treeNodes,
        expandedNodes: expanded,
        filterGroup,
        defaultFilterIds,
        activeDateField: model.date_field || null,
        dateRangePreset: defaultPreset,
        dateRangeStart: presetDates ? presetDates.start : null,
        dateRangeEnd: presetDates ? presetDates.end : null,
        isLoading: false,
      });
    } catch (err: any) {
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({
        error: err?.message || 'Failed to load model',
        isLoading: false,
      });
    }
  },

  // ----------------------------------------------------------
  // Selection actions
  // ----------------------------------------------------------

  toggleMetric: (metricName: string) => {
    const { selectedMetrics } = get();
    const newInstance: MetricInstance = { id: generateId(), metricName };
    set({
      selectedMetrics: [...selectedMetrics, newInstance],
      querySQL: '',
      queryResults: null,
      activeResultTab: null,
    });
  },

  removeMetricInstance: (id: string) => {
    const { selectedMetrics, metricConfigs, orderBy } = get();
    const removed = selectedMetrics.find((m) => m.id === id);
    const newConfigs = { ...metricConfigs };
    delete newConfigs[id];
    // Prune any order_by entry that was keyed on this instance's primary sort column.
    let newOrderBy = orderBy;
    if (removed) {
      const sortCol = getMetricPrimarySortColumn(removed.metricName, metricConfigs[id]);
      newOrderBy = orderBy.filter((o) => o.column !== sortCol);
    }
    set({
      selectedMetrics: selectedMetrics.filter((m) => m.id !== id),
      metricConfigs: newConfigs,
      orderBy: newOrderBy,
      querySQL: '',
      queryResults: null,
      activeResultTab: null,
    });
  },

  toggleDimension: (dimensionName: string) => {
    const { selectedDimensions, orderBy } = get();
    const index = selectedDimensions.indexOf(dimensionName);
    if (index >= 0) {
      const alias = getDimensionOutputAlias(dimensionName);
      set({
        selectedDimensions: selectedDimensions.filter(
          (d) => d !== dimensionName
        ),
        orderBy: orderBy.filter((o) => o.column !== alias),
        querySQL: '',
        queryResults: null,
        activeResultTab: null,
      });
    } else {
      set({ selectedDimensions: [...selectedDimensions, dimensionName], querySQL: '', queryResults: null, activeResultTab: null });
    }
  },

  setMetricAlias: (instanceId: string, alias: string) => {
    const { metricConfigs, selectedMetrics, orderBy } = get();
    const instance = selectedMetrics.find((m) => m.id === instanceId);
    if (!instance) return;
    const oldSort = getMetricPrimarySortColumn(instance.metricName, metricConfigs[instanceId]);
    const configs = { ...metricConfigs };
    configs[instanceId] = { ...(configs[instanceId] || { metricName: instance.metricName, instanceId }), alias: alias || undefined };
    const newSort = getMetricPrimarySortColumn(instance.metricName, configs[instanceId]);
    const newOrderBy = oldSort !== newSort
      ? orderBy.map((o) => (o.column === oldSort ? { ...o, column: newSort } : o))
      : orderBy;
    set({ metricConfigs: configs, orderBy: newOrderBy, querySQL: '', queryResults: null, activeResultTab: null });
  },

  setMetricAggregationOverride: (instanceId: string, agg: string | undefined) => {
    const { metricConfigs, selectedMetrics } = get();
    const instance = selectedMetrics.find((m) => m.id === instanceId);
    if (!instance) return;
    const configs = { ...metricConfigs };
    configs[instanceId] = { ...(configs[instanceId] || { metricName: instance.metricName, instanceId }), aggregationOverride: agg };
    set({ metricConfigs: configs, querySQL: '', queryResults: null, activeResultTab: null });
  },

  setMetricTimePeriod: (instanceId: string, period: string | undefined) => {
    const { metricConfigs, selectedMetrics, orderBy } = get();
    const instance = selectedMetrics.find((m) => m.id === instanceId);
    if (!instance) return;
    const oldSort = getMetricPrimarySortColumn(instance.metricName, metricConfigs[instanceId]);
    const configs = { ...metricConfigs };
    configs[instanceId] = { ...(configs[instanceId] || { metricName: instance.metricName, instanceId }), timePeriod: period };
    const newSort = getMetricPrimarySortColumn(instance.metricName, configs[instanceId]);
    const newOrderBy = oldSort !== newSort
      ? orderBy.map((o) => (o.column === oldSort ? { ...o, column: newSort } : o))
      : orderBy;
    set({ metricConfigs: configs, orderBy: newOrderBy, querySQL: '', queryResults: null, activeResultTab: null });
  },

  setMetricDateDimension: (instanceId: string, dateDim: string | undefined) => {
    const { metricConfigs, selectedMetrics } = get();
    const instance = selectedMetrics.find((m) => m.id === instanceId);
    if (!instance) return;
    const configs = { ...metricConfigs };
    configs[instanceId] = { ...(configs[instanceId] || { metricName: instance.metricName, instanceId }), dateDimension: dateDim };
    set({ metricConfigs: configs, querySQL: '', queryResults: null, activeResultTab: null });
  },

  addFilter: (filter: QueryFilter) => {
    const fg = get().filterGroup;
    const newFg = addToGroup(fg, fg.id, createFilterCondition(filter.column, filter.operator, filter.value));
    set({ filters: [...get().filters, filter], filterGroup: newFg, querySQL: '', queryResults: null, activeResultTab: null });
  },

  removeFilter: (index: number) => {
    const newFilters = get().filters.filter((_, i) => i !== index);
    // Rebuild filterGroup from remaining flat filters for legacy compat
    set({ filters: newFilters, filterGroup: filtersToGroup(newFilters), querySQL: '', queryResults: null, activeResultTab: null });
  },

  // ----------------------------------------------------------
  // Nested filter actions
  // ----------------------------------------------------------

  addFilterCondition: (parentGroupId, column, operator, value, value2) => {
    const fg = get().filterGroup;
    const newFg = addToGroup(fg, parentGroupId, createFilterCondition(column, operator, value, value2));
    set({ filterGroup: newFg, querySQL: '', queryResults: null, activeResultTab: null });
  },

  addFilterGroup: (parentGroupId) => {
    const fg = get().filterGroup;
    const newChild = createEmptyFilterGroup();
    const newFg = addToGroup(fg, parentGroupId, newChild);
    set({ filterGroup: newFg, querySQL: '', queryResults: null, activeResultTab: null });
  },

  removeFilterNode: (nodeId) => {
    // Guard: cannot remove default (locked) filters
    if (get().defaultFilterIds.has(nodeId)) return;
    const fg = get().filterGroup;
    const newFg = removeFromGroup(fg, nodeId);
    set({ filterGroup: newFg, querySQL: '', queryResults: null, activeResultTab: null });
  },

  updateFilterCondition: (conditionId, updates) => {
    // Guard: cannot edit default (locked) filters
    if (get().defaultFilterIds.has(conditionId)) return;
    const fg = get().filterGroup;
    const newFg = updateInGroup(fg, conditionId, (node) => {
      if (node.type === 'condition') {
        return { ...node, ...updates };
      }
      return node;
    });
    set({ filterGroup: newFg, querySQL: '', queryResults: null, activeResultTab: null });
  },

  toggleFilterGroupLogic: (groupId) => {
    const fg = get().filterGroup;
    const newFg = updateInGroup(fg, groupId, (node) => {
      if (node.type === 'group') {
        return { ...node, logic: node.logic === 'AND' ? 'OR' : 'AND' };
      }
      return node;
    }) as FilterGroup;
    // Handle root group toggle
    const result = fg.id === groupId
      ? { ...fg, logic: (fg.logic === 'AND' ? 'OR' : 'AND') as 'AND' | 'OR' }
      : newFg;
    set({ filterGroup: result, querySQL: '', queryResults: null, activeResultTab: null });
  },

  toggleFilterGroupNegated: (groupId) => {
    const fg = get().filterGroup;
    const newFg = updateInGroup(fg, groupId, (node) => {
      if (node.type === 'group') {
        return { ...node, negated: !node.negated };
      }
      return node;
    }) as FilterGroup;
    const result = fg.id === groupId
      ? { ...fg, negated: !fg.negated }
      : newFg;
    set({ filterGroup: result, querySQL: '', queryResults: null, activeResultTab: null });
  },

  setFilterGroup: (group) => {
    set({ filterGroup: group, querySQL: '', queryResults: null, activeResultTab: null });
  },

  addCalculatedField: ({ name, formula, type = 'dimension' }) => {
    const attr: CalculatedAttribute = {
      id: generateId(),
      name,
      formula,
      type,
    };
    set({
      calculatedAttributes: [...get().calculatedAttributes, attr],
      querySQL: '',
      queryResults: null,
      activeResultTab: null,
    });
  },

  updateCalculatedField: (id, updates) => {
    set({
      calculatedAttributes: get().calculatedAttributes.map((a) =>
        a.id === id ? { ...a, ...updates } : a
      ),
      querySQL: '',
      queryResults: null,
      activeResultTab: null,
    });
  },

  removeCalculatedField: (id) => {
    set({
      calculatedAttributes: get().calculatedAttributes.filter((a) => a.id !== id),
      querySQL: '',
      queryResults: null,
      activeResultTab: null,
    });
  },

  clearSelections: () => {
    const { filterGroup, defaultFilterIds, semanticModel } = get();
    // Retain default (locked) filter conditions, remove everything else
    let newFilterGroup = createEmptyFilterGroup();
    if (defaultFilterIds.size > 0) {
      const defaultConditions = filterGroup.children.filter(
        (child) => child.type === 'condition' && defaultFilterIds.has(child.id)
      );
      newFilterGroup = { ...newFilterGroup, children: defaultConditions };
    }
    set({
      selectedMetrics: [],
      selectedDimensions: [],
      metricConfigs: {},
      calculatedAttributes: [],
      filters: [],
      filterGroup: newFilterGroup,
      orderBy: [],
      querySQL: '',
      queryResults: null,
      activeResultTab: null,
      loadedReportId: null,
      loadedReportName: null,
      activeDateField: semanticModel?.date_field || null,
      dateRangePreset: 'YESTERDAY' as DateRangePreset,
      dateRangeStart: semanticModel?.date_field
        ? resolvePresetDates('YESTERDAY').start
        : null,
      dateRangeEnd: semanticModel?.date_field
        ? resolvePresetDates('YESTERDAY').end
        : null,
    });
  },

  applyAgentPatchToWorkspace: async (patch: WorkspaceAgentPatch) => {
    const state = get();
    const next: Partial<WorkspaceSlice> = {};

    // Perspective switch — delegate to selectPerspective so the model loads
    if (patch.set_perspective_id && patch.set_perspective_id !== state.selectedPerspective) {
      await get().selectPerspective(patch.set_perspective_id);
    }

    // Metrics
    let metrics = get().selectedMetrics;
    if (patch.replace_metrics) {
      metrics = patch.replace_metrics.map((name) => ({ id: generateId(), metricName: name }));
    } else {
      if (patch.remove_metrics && patch.remove_metrics.length) {
        const toRemove = new Set(patch.remove_metrics);
        metrics = metrics.filter((m) => !toRemove.has(m.metricName));
      }
      if (patch.add_metrics && patch.add_metrics.length) {
        const existing = new Set(metrics.map((m) => m.metricName));
        const additions = patch.add_metrics
          .filter((name) => !existing.has(name))
          .map((name) => ({ id: generateId(), metricName: name }));
        metrics = [...metrics, ...additions];
      }
    }
    if (metrics !== get().selectedMetrics) next.selectedMetrics = metrics;

    // Dimensions
    let dimensions = get().selectedDimensions;
    if (patch.replace_dimensions) {
      dimensions = [...patch.replace_dimensions];
    } else {
      if (patch.remove_dimensions && patch.remove_dimensions.length) {
        const toRemove = new Set(patch.remove_dimensions);
        dimensions = dimensions.filter((d) => !toRemove.has(d));
      }
      if (patch.add_dimensions && patch.add_dimensions.length) {
        const existing = new Set(dimensions);
        const additions = patch.add_dimensions.filter((d) => !existing.has(d));
        dimensions = [...dimensions, ...additions];
      }
    }
    if (dimensions !== get().selectedDimensions) next.selectedDimensions = dimensions;

    // Filters — simple list form (filters[]). filterGroup is kept untouched.
    let filters = get().filters;
    if (patch.replace_filters) {
      filters = patch.replace_filters.map((f) => ({ column: f.column, operator: f.operator, value: f.value }));
    } else if (patch.add_filters && patch.add_filters.length) {
      filters = [...filters, ...patch.add_filters.map((f) => ({ column: f.column, operator: f.operator, value: f.value }))];
    }
    if (filters !== get().filters) next.filters = filters;

    // Date context
    if (patch.set_active_date_field !== undefined && patch.set_active_date_field !== null) {
      next.activeDateField = patch.set_active_date_field;
    }
    if (patch.set_date_range_preset) {
      const preset = patch.set_date_range_preset as DateRangePreset;
      const dates = resolvePresetDates(preset);
      next.dateRangePreset = preset;
      if (dates) {
        next.dateRangeStart = dates.start;
        next.dateRangeEnd = dates.end;
      }
    }
    if (patch.set_date_range_start) next.dateRangeStart = patch.set_date_range_start;
    if (patch.set_date_range_end) next.dateRangeEnd = patch.set_date_range_end;

    set(next as any);

    if (patch.execute) {
      await get().executeQuery();
    }
  },

  setActiveDateField: (field: string | null) => {
    set({ activeDateField: field, querySQL: '', queryResults: null });
  },

  setDateRange: (start: string | null, end: string | null) => {
    set({ dateRangeStart: start, dateRangeEnd: end, dateRangePreset: 'CUSTOM' as DateRangePreset, querySQL: '', queryResults: null });
  },

  setDateRangePreset: (preset: DateRangePreset) => {
    if (preset === 'CUSTOM') {
      set({ dateRangePreset: preset });
      return;
    }
    const { start, end } = resolvePresetDates(preset);
    set({ dateRangePreset: preset, dateRangeStart: start, dateRangeEnd: end, querySQL: '' });
  },

  // ----------------------------------------------------------
  // Sort (ORDER BY) actions — user-dictated only, never auto-generated
  // ----------------------------------------------------------

  setOrderByDirection: (column: string, direction: OrderByDirection | null) => {
    const { orderBy } = get();
    const existingIdx = orderBy.findIndex((o) => o.column === column);
    let next: OrderBySpec[];
    if (direction === null) {
      if (existingIdx < 0) return;
      next = orderBy.filter((o) => o.column !== column);
    } else if (existingIdx >= 0) {
      next = orderBy.map((o) => (o.column === column ? { ...o, direction } : o));
    } else {
      next = [...orderBy, { column, direction }];
    }
    set({ orderBy: next, querySQL: '', queryResults: null, activeResultTab: null });
  },

  cycleOrderBy: (column: string) => {
    const { orderBy } = get();
    const existing = orderBy.find((o) => o.column === column);
    // Cycle: none → ASC → DESC → none
    const nextDir: OrderByDirection | null =
      !existing ? 'ASC' : existing.direction === 'ASC' ? 'DESC' : null;
    get().setOrderByDirection(column, nextDir);
  },

  moveOrderBy: (column: string, delta: number) => {
    const { orderBy } = get();
    const idx = orderBy.findIndex((o) => o.column === column);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= orderBy.length) return;
    const next = [...orderBy];
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved);
    set({ orderBy: next, querySQL: '', queryResults: null, activeResultTab: null });
  },

  removeOrderBy: (column: string) => {
    const { orderBy } = get();
    if (!orderBy.some((o) => o.column === column)) return;
    set({
      orderBy: orderBy.filter((o) => o.column !== column),
      querySQL: '',
      queryResults: null,
      activeResultTab: null,
    });
  },

  clearOrderBy: () => {
    if (get().orderBy.length === 0) return;
    set({ orderBy: [], querySQL: '', queryResults: null, activeResultTab: null });
  },

  // ----------------------------------------------------------
  // Query actions
  // ----------------------------------------------------------

  generateQuery: async () => {
    const { selectedPerspective, selectedMetrics, selectedDimensions, filterGroup, defaultFilterIds, metricConfigs, calculatedAttributes, userSettings, activeDraftId, activeDateField, dateRangeStart, dateRangeEnd, dateRangePreset, orderBy } =
      get();

    if (!selectedPerspective) {
      set({ error: 'No perspective selected' });
      return;
    }

    if (selectedMetrics.length === 0) {
      set({ error: 'Please select at least one metric' });
      return;
    }

    // Block generation when a date-context model has no date range set.
    // A non-CUSTOM preset satisfies this check (dates are resolved by the backend).
    // Exempt queries where any metric has a timePeriod (those handle their own date scoping).
    if (activeDateField && !dateRangeStart && !dateRangeEnd && dateRangePreset === 'CUSTOM') {
      const hasTimePeriod = selectedMetrics.some(
        (m) => metricConfigs[m.id]?.timePeriod
      );
      if (!hasTimePeriod) {
        set({ error: 'Please set a date range before generating SQL.' });
        return;
      }
    }

    set({ isLoading: true, error: null, errorDetails: null });
    try {
      // Deduplicate dimensions (keep granularity suffixes like __year, __month)
      const uniqueDimensions = Array.from(new Set(selectedDimensions));

      // Strip default filters before sending to backend (backend adds them server-side)
      const userFilterGroup = defaultFilterIds.size > 0
        ? stripDefaultFilters(filterGroup, defaultFilterIds)
        : filterGroup;

      // Serialize filter group for backend
      const apiFilters = hasConditions(userFilterGroup)
        ? serializeFilterGroup(userFilterGroup)
        : undefined;

      // Build metricConfigs array from the map, only include entries with overrides
      const configsArray: MetricConfig[] = Object.values(metricConfigs).filter(
        (c) => c.alias || c.aggregationOverride || c.timePeriod || c.dateDimension
      );

      const isDraftMode = userSettings.modelSource === 'draft' && activeDraftId;
      const result = await generateQuery({
        perspective: selectedPerspective,
        metrics: selectedMetrics.map((m) => ({ name: m.metricName, instanceId: m.id })),
        dimensions: uniqueDimensions,
        filters: apiFilters,
        ...(configsArray.length > 0 ? { metricConfigs: configsArray } : {}),
        ...(calculatedAttributes.length > 0 ? { calculatedAttributes } : {}),
        limit: userSettings.resultLimit,
        ...(isDraftMode ? { draftId: activeDraftId } : {}),
        ...(activeDateField ? { dateField: activeDateField } : {}),
        ...(dateRangeStart ? { dateRangeStart } : {}),
        ...(dateRangeEnd ? { dateRangeEnd } : {}),
        ...(dateRangePreset && dateRangePreset !== 'CUSTOM' ? { dateRangePreset } : {}),
        ...(orderBy.length > 0 ? { orderBy } : {}),
      });
      set({ querySQL: result.sql, isLoading: false });
    } catch (err: any) {
      const serverError = err?.response?.data?.error;
      const serverDetails = err?.response?.data?.errorDetails as ErrorDetails | undefined;
      set({
        error: serverError || err?.message || 'Failed to generate query',
        errorDetails: serverDetails || null,
        isLoading: false,
      });
    }
  },

  executeQuery: async () => {
    const namespace = getPersistenceNamespace(), model = get().semanticModel;
    const stale = () => namespace !== getPersistenceNamespace() || model !== get().semanticModel;
    const run = ++queryRun;
    const phase = (queryPhase: QueryPhase) => { if (run === queryRun) set({ queryPhase }); };
    // 1. Immediately clear stale state and show loading
    set({ queryResults: null, activeResultTab: null, querySQL: '', isLoading: true, error: null, errorDetails: null, queryPhase: 'generating' });

    // 2. Always regenerate SQL from current state. No caching.
    let sql: string;
    let sqlDialect: string | undefined;
    let readings: QueryResult['readings'];
    const { selectedPerspective, selectedMetrics, selectedDimensions, filterGroup,
            defaultFilterIds, metricConfigs, calculatedAttributes, userSettings, activeDraftId, activeDateField,
            dateRangeStart, dateRangeEnd, dateRangePreset, orderBy } = get();

    if (!selectedPerspective) {
      set({ error: 'No perspective selected', isLoading: false });
      phase('idle');
      return;
    }
    if (selectedMetrics.length === 0) {
      set({ error: 'Please select at least one metric', isLoading: false });
      phase('idle');
      return;
    }

    try {
      const uniqueDimensions = Array.from(new Set(selectedDimensions));
      const userFilterGroup = defaultFilterIds.size > 0
        ? stripDefaultFilters(filterGroup, defaultFilterIds) : filterGroup;
      const apiFilters = hasConditions(userFilterGroup)
        ? serializeFilterGroup(userFilterGroup) : undefined;
      const configsArray = Object.values(metricConfigs).filter(
        (c) => c.alias || c.aggregationOverride || c.timePeriod || c.dateDimension
      );
      const isDraftMode = userSettings.modelSource === 'draft' && activeDraftId;

      const result = await generateQuery({
        perspective: selectedPerspective, metrics: selectedMetrics.map((m) => ({ name: m.metricName, instanceId: m.id })),
        dimensions: uniqueDimensions, filters: apiFilters,
        ...(configsArray.length > 0 ? { metricConfigs: configsArray } : {}),
        ...(calculatedAttributes.length > 0 ? { calculatedAttributes } : {}),
        limit: userSettings.resultLimit,
        ...(isDraftMode ? { draftId: activeDraftId } : {}),
        ...(activeDateField ? { dateField: activeDateField } : {}),
        ...(dateRangeStart ? { dateRangeStart } : {}),
        ...(dateRangeEnd ? { dateRangeEnd } : {}),
        ...(dateRangePreset && dateRangePreset !== 'CUSTOM' ? { dateRangePreset } : {}),
        ...(orderBy.length > 0 ? { orderBy } : {}),
      });
      if (stale()) { phase('idle'); return; }
      sql = result.sql;
      sqlDialect = result.meta?.sqlDialect;
      readings = result.readings;
      set({ querySQL: sql });
      phase('executing');
    } catch (err: any) {
      phase('idle');
      if (stale()) return;
      const serverError = err?.response?.data?.error;
      const serverDetails = err?.response?.data?.errorDetails as ErrorDetails | undefined;
      set({
        error: serverError || err?.message || 'Failed to generate query',
        errorDetails: serverDetails || null,
        isLoading: false,
      });
      return;
    }

    // 3. Execute
    try {
      // Snapshot workspace context for server-side error logging — mirrors
      // the fields the diagnostic report copy button captures, so backend
      // logs and the user's pasted report stay in sync.
      const userFilterGroupForCtx = defaultFilterIds.size > 0
        ? stripDefaultFilters(filterGroup, defaultFilterIds) : filterGroup;
      const filtersForCtx = hasConditions(userFilterGroupForCtx)
        ? serializeFilterGroup(userFilterGroupForCtx) : null;
      const requestContext = {
        selectedDomain: get().selectedDomain,
        selectedPerspective,
        activeDraftId: activeDraftId ?? null,
        modelSource: userSettings?.modelSource,
        resultLimit: userSettings?.resultLimit,
        activeDateField: activeDateField ?? null,
        dateRangePreset: dateRangePreset ?? null,
        dateRangeStart: dateRangeStart ?? null,
        dateRangeEnd: dateRangeEnd ?? null,
        selectedMetrics: selectedMetrics.map((m) => ({ metricName: m.metricName, id: m.id })),
        selectedDimensions,
        calculatedAttributes,
        orderBy,
        filters: filtersForCtx,
      };
      const results = await executeQuery(sql, requestContext, sqlDialect);
      phase('idle');
      if (stale()) return;
      // the reading line rides on these results only, so it is cleared with them and never persisted (#1818)
      set({ queryResults: readings ? { ...results, readings } : results, isLoading: false });
    } catch (err: any) {
      phase('idle');
      if (stale()) return;
      const serverError = err?.response?.data?.error;
      const serverDetails = err?.response?.data?.errorDetails as ErrorDetails | undefined;
      set({
        error: serverError || err?.message || 'Failed to execute query',
        errorDetails: serverDetails || null,
        isLoading: false,
      });
    }
  },

  // ----------------------------------------------------------
  // Navigation
  // ----------------------------------------------------------

  navigateToNode: (nodeId: string) => {
    const { expandedNodes, treeNodes } = get();

    // Expand all ancestors of the target node
    const newExpanded = new Set(expandedNodes);

    const findAndExpand = (
      nodes: TreeNode[],
      targetId: string,
      path: string[]
    ): boolean => {
      for (const node of nodes) {
        if (node.id === targetId) {
          path.forEach((p) => newExpanded.add(p));
          return true;
        }
        if (node.children) {
          if (findAndExpand(node.children, targetId, [...path, node.id])) {
            return true;
          }
        }
      }
      return false;
    };

    findAndExpand(treeNodes, nodeId, []);

    set({
      expandedNodes: newExpanded,
      focusedNodeId: nodeId,
    });
  },

  toggleModelBrowser: () => {
    set({ isModelBrowserOpen: !get().isModelBrowserOpen });
  },

  // ----------------------------------------------------------
  // Pinned Results actions
  // ----------------------------------------------------------

  pinCurrentResults: () => {
    const { queryResults, querySQL, selectedMetrics, selectedDimensions, filterGroup, pinnedResults } = get();
    if (!queryResults) return;

    // Build a readable filter summary from the filter group
    const summarizeGroup = (group: FilterGroup): string => {
      const parts: string[] = [];
      for (const child of group.children) {
        if (child.type === 'condition') {
          if (child.column) {
            const needsNoValue = child.operator === 'IS NULL' || child.operator === 'IS NOT NULL';
            if (needsNoValue) {
              parts.push(`${child.column} ${child.operator}`);
            } else if (child.operator === 'BETWEEN' && child.value2) {
              parts.push(`${child.column} BETWEEN ${child.value} AND ${child.value2}`);
            } else if (child.value) {
              parts.push(`${child.column} ${child.operator} ${child.value}`);
            }
          }
        } else if (child.type === 'group') {
          const sub = summarizeGroup(child);
          if (sub) parts.push(`(${sub})`);
        }
      }
      const joiner = group.logic === 'OR' ? ' OR ' : ', ';
      const result = parts.join(joiner);
      return group.negated && result ? `NOT (${result})` : result;
    };

    const filterSummary = summarizeGroup(filterGroup);
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const pinNumber = pinnedResults.length + 1;

    const pin: PinnedResult = {
      id: crypto.randomUUID(),
      label: `Pin ${pinNumber} - ${timeStr}`,
      pinnedAt: now.toISOString(),
      queryResults: { ...queryResults },
      querySQL,
      selectedMetrics: selectedMetrics.map((m) => m.metricName),
      selectedDimensions: [...selectedDimensions],
      filterSummary,
    };

    set({ pinnedResults: [...pinnedResults, pin] });
  },

  unpinResult: (id: string) => {
    const { pinnedResults, activeResultTab } = get();
    set({
      pinnedResults: pinnedResults.filter((p) => p.id !== id),
      activeResultTab: activeResultTab === id ? null : activeResultTab,
    });
  },

  renamePin: (id: string, label: string) => {
    set({
      pinnedResults: get().pinnedResults.map((p) =>
        p.id === id ? { ...p, label } : p
      ),
    });
  },

  setActiveResultTab: (id: string | null) => {
    set({ activeResultTab: id });
  },

  // ----------------------------------------------------------
  // Template actions
  // ----------------------------------------------------------

  applyTemplate: (template: QueryTemplate) => {
    set({
      selectedMetrics: template.metrics.map((name) => ({ id: generateId(), metricName: name })),
      selectedDimensions: [...template.dimensions],
      metricConfigs: {},
      filters: [...template.filters],
      filterGroup: filtersToGroup(template.filters),
      orderBy: [],
      querySQL: '',
      queryResults: null,
      activeResultTab: null,
    });
  },
});
